/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import {
  Button,
  Divider,
  Form,
  Input,
  PageHeader,
  Select,
  Tooltip,
} from 'antd';
import { withTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { Colorpicker } from 'antd-colorpicker';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 16 },
    lg: { span: 8 },
    xl: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 12 },
    lg: { span: 12 },
    xl: { span: 12 },
  },
};

const buttonFormItem = {
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 12 },
    md: { span: 16 },
    lg: { span: 20 },
    xl: { span: 20 },
  },
};

const modules = {
  toolbar: [['bold', 'italic', 'underline', 'strike'], ['link']],
};

const formats = ['bold', 'italic', 'underline', 'strike', 'link'];

class ReportingMailAlerts extends React.Component {
  formRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      footerText: '',
    };
  }

  componentDidMount() {
    const { platformConfigData } = this.props;
    if (platformConfigData) {
      this.formRef.current.setFieldsValue(platformConfigData);
    }
  }

  onFinish = values => {
    const { userDomain } = this.props.config;
    const configPayload = {};
    values.tenantDomain = userDomain;
    configPayload.metaValue = JSON.stringify(values);
    this.props.callback(configPayload);
  };

  setFooterText = value => {
    this.setState({ footerText: value });
  };

  render() {
    const { t } = this.props;
    const { footerText } = this.state;
    return (
      <div>
        <div>
          <Divider>
            <PageHeader
              className="site-page-header-responsive"
              subTitle={t('label_reporting_mail_config')}
            />
          </Divider>
        </div>
        <div>
          <Form
            {...formItemLayout}
            name="ReportingConfigurationForm"
            onFinish={this.onFinish}
            ref={this.formRef}
          >
            <Form.Item
              label={t('label_reporting_mail_sender')}
              name="mailSender"
              rules={[
                {
                  required: true,
                  message: t('form_generalRequiredMsg', {
                    label: t('label_reporting_mail_sender'),
                  }),
                },
              ]}
            >
              <Input placeholder={'example@domain.com'} />
            </Form.Item>
            <Form.Item
              label={t('label_reporting_mail_sender_name')}
              name="mailSenderName"
            >
              <Input placeholder={t('label_mail_alerts')} />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  {t('label_reporting_mail_sender_password')}&nbsp;
                  <Tooltip
                    title={
                      <div>
                        <div>
                          {t('label_reporting_mail_sender_password_tooltip')}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <a
                            href={
                              'https://support.google.com/accounts/answer/185833'
                            }
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {' '}
                            {t('label_SeeMoreDetails')}
                          </a>
                        </div>
                      </div>
                    }
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </span>
              }
              name="mailSenderPassword"
              rules={[
                {
                  required: true,
                  message: t('form_generalRequiredMsg', {
                    label: t('label_reporting_mail_sender_password'),
                  }),
                },
              ]}
            >
              <Input type="password" />
            </Form.Item>
            <Form.Item
              label={t('label_mailReceivers')}
              name="mailReceivers"
              rules={[
                {
                  required: true,
                  message: t('form_generalRequiredMsg', {
                    label: t('label_mailReceivers'),
                  }),
                },
                {
                  validator(rule, value) {
                    const pattern = /^\w+@[a-zA-Z_]+?\.[a-zA-Z]{2,3}$/;
                    let isValid = true;
                    if (value) {
                      value.forEach(email => {
                        if (!pattern.test(email)) {
                          isValid = false;
                        }
                      });
                    }
                    if (!isValid) {
                      return Promise.reject(t('invalidEmail_txt'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                tokenSeparators={[',']}
              />
            </Form.Item>
            <Form.Item
              label={t('label_mailSubject')}
              name="mailSubject"
              rules={[
                {
                  required: true,
                  message: t('form_generalRequiredMsg', {
                    label: t('label_mailSubject'),
                  }),
                },
              ]}
            >
              <Input placeholder={t('form_addMailSubject')} />
            </Form.Item>
            <div>
              <Divider>
                <PageHeader
                  className="site-page-header-responsive"
                  subTitle={t('label_reporting_mail_config_content')}
                />
              </Divider>
            </div>
            <Form.Item label={t('label_mailLogoUrl')} name="mailLogoUrl">
              <Input placeholder="https://domain.com/logo.png" />
            </Form.Item>
            <Form.Item label={t('label_mailGreeting')} name="mailGreeting">
              <Input placeholder={t('form_addMailGreeting')} />
            </Form.Item>
            <Form.Item label={t('label_mailBody')} name="mailBody">
              <TextArea rows={4} />
            </Form.Item>
            <Form.Item label={t('label_mailSignature')} name="mailSignature">
              <TextArea rows={4} />
            </Form.Item>
            <Form.Item
              label={t('label_mailFooterText')}
              name="mailFooterText"
              initialValue={footerText || ''}
            >
              <ReactQuill
                theme="snow"
                value={footerText || ''}
                modules={modules}
                formats={formats}
                onChange={this.setFooterText}
                placeholder={t('label_theme_footer_formPlaceholder')}
              />
            </Form.Item>
            <div>
              <Divider>
                <PageHeader
                  className="site-page-header-responsive"
                  subTitle={t('label_reporting_mail_config_color_theme')}
                />
              </Divider>
            </div>
            <Form.Item
              label={t('label_mailHeaderColor')}
              name="mailHeaderColor"
            >
              <Colorpicker
                popup
                picker={'GooglePicker'}
                onColorResult={color => color.hex}
              />
            </Form.Item>
            <Form.Item
              label={t('label_mailFooterColor')}
              name="mailFooterColor"
            >
              <Colorpicker
                popup
                picker={'GooglePicker'}
                onColorResult={color => color.hex}
              />
            </Form.Item>
            <Form.Item {...buttonFormItem}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ float: 'right' }}
              >
                {t('label_save')}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    );
  }
}

export default withTranslation()(ReportingMailAlerts);
